import { createContext } from 'react'
import type { FontFamily } from '@/api/types/font-family'
import type { LoadedFontVariant } from '@/lib/fonts/font-loader'

export type FontContextValue = {
  /** Families used on the current spread (eager fetch). */
  usedFamilies: FontFamily[]
  /** Full catalog; populated after `loadFullCatalog()` (e.g. font picker open). */
  allFamilies: FontFamily[]
  /** @deprecated Prefer `usedFamilies` / `allFamilies`; merged map for lookups. */
  families: FontFamily[]
  isUsedCatalogLoading: boolean
  /**
   * True only once the used-families query has resolved with data. False while loading, on error,
   * and after logout clears the query cache (the query is then disabled, so it is neither loading
   * nor loaded). Use this, not `!isUsedCatalogLoading`, before treating `usedFamilies` as complete.
   */
  isUsedCatalogReady: boolean
  isFullCatalogLoading: boolean
  isError: boolean
  error: Error | null
  /** CSS family names (FontFamily.familyName) whose woff files failed to load. */
  failedFontFamilyNames: ReadonlySet<string>
  /**
   * Monotonic counter, bumped every time new `FontFace`s actually land in `document.fonts`.
   *
   * Anything that PRE-MEASURES text — the off-screen line-break/height mirrors in
   * `lib/text/paragraph-visual-rows.ts` and `lib/text/fit-paragraphs-to-box.ts`, and
   * `use-shape-text-layout.ts`'s canvas measurer — must recompute when this changes.
   * `getFamilyCssName` starts returning the real family name the moment the catalog query resolves,
   * which is one render BEFORE the provider's effect has even begun fetching the woff, so any
   * measurement taken then used the `sans-serif` fallback's metrics and nothing afterwards
   * invalidated it. Consumers pick this up for free by depending on the whole context object.
   *
   * `document.fonts.ready` and the `loadingdone` event CANNOT serve as this signal:
   * `font-loader.ts` calls `face.load()` BEFORE `document.fonts.add(face)`, and per CSS Font
   * Loading L3 `add()` only switches the FontFaceSet to "loading" when the added face's status is
   * already "loading". Adding an already-loaded face is silent, so the set never re-enters loading,
   * `loadingdone` never fires, and `ready` never re-arms after app boot.
   */
  facesLoadedGeneration: number
  loadFullCatalog: () => void
  ensureFamilyFacesLoaded: (fontFamilyId: number) => Promise<void>
  getFamilyById: (fontFamilyId: number) => FontFamily | undefined
  getVariantByFontId: (fontId: number | undefined) => LoadedFontVariant | undefined
  getFamilyCssName: (fontFamilyId: number | undefined) => string | undefined
  getFontIdByVariant: (fontFamilyId: number, bold: boolean, italic: boolean) => number | undefined
  getFamilyIdByCssName: (cssName: string) => number | undefined
  pickDefaultFontIdForFamilyId: (fontFamilyId: number) => number | undefined
  /** Returns true when every font face in the family failed to load (e.g. blocked by CSP). */
  isFamilyFailed: (fontFamilyId: number) => boolean
}

export const FontContext = createContext<FontContextValue | null>(null)
